import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';

interface StatConfig {
  key: string;
  label: string;
  format?: (value: unknown) => string;
  optional?: boolean;
  bold?: boolean;
}

const OFFSET = 12;

export function ScatterPagePreviewTooltip({
  active,
  payload,
  pageIdKey,
  stats,
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown> }>;
  pageIdKey: string;
  stats: StatConfig[];
}) {
  const [loaded, setLoaded] = useState(false);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const data = payload?.[0]?.payload;
  const pageId = data?.[pageIdKey] as string | undefined;

  useEffect(() => {
    setLoaded(false);
  }, [pageId]);

  // Track mouse and position tooltip via rAF (no state, no re-renders)
  useEffect(() => {
    if (!active) return;

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(updatePosition);
      }
    };

    const updatePosition = () => {
      rafRef.current = 0;
      const el = tooltipRef.current;
      if (!el) return;

      const { x, y } = mouseRef.current;
      const rect = el.getBoundingClientRect();
      const midX = window.innerWidth / 2;

      // Show on opposite side of cursor
      const left = x < midX ? x + OFFSET : x - rect.width - OFFSET;
      const top = Math.max(4, Math.min(y - rect.height / 2, window.innerHeight - rect.height - 4));

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    };

    document.addEventListener('mousemove', onMouseMove);
    // Position immediately with current mouse pos
    requestAnimationFrame(updatePosition);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  if (!active || !payload?.length) return null;

  const pageNumber = data?.pageNumber as number | undefined;

  return createPortal(
    <div ref={tooltipRef} className="pointer-events-none fixed z-9999 top-0 left-0">
      <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded shadow overflow-hidden flex">
        {pageId && (
          <div className="w-[120px] shrink-0 bg-gray-100 dark:bg-zinc-700 relative aspect-3/4">
            <img
              src={pagePreviewSrc(pageId, { resize: 'x400' })}
              alt={`Page ${pageNumber ?? ''}`}
              className={`w-full h-full object-contain transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setLoaded(true)}
            />
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 dark:border-indigo-400" />
              </div>
            )}
          </div>
        )}
        <div className="p-2 text-xs space-y-0.5 self-center">
          {pageNumber != null && <p className="font-bold">Page {pageNumber}</p>}
          {stats.map((stat) => {
            const value = data?.[stat.key];
            if (stat.optional && value == null) return null;
            const formatted = stat.format ? stat.format(value) : String(value ?? '');
            return (
              <p
                key={stat.key}
                className={
                  stat.bold
                    ? 'font-bold text-gray-700 dark:text-zinc-100'
                    : 'text-gray-600 dark:text-zinc-400'
                }
              >
                {stat.label ? `${stat.label}: ${formatted}` : formatted}
              </p>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
